// src/pages/auth/OAuthCallbackPage.tsx
// Handles OAuth2 callback from external providers (Google, GitHub, Microsoft, etc.).

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useAuthn, useAuth, useI18n } from '@react/hooks';
import { authStore } from '@core/stores';
import { ErrorAlert } from '@react/components';
import { AuthLayout } from './AuthLayout';

export const OAuthCallbackPage = observer(() => {
  const authnStore = useAuthn();
  useAuth(); // keep reactivity for auth state
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError(searchParams.get('error_description') || errorParam);
      return;
    }

    if (!code) {
      setError(t('auth.oauthMissingCode'));
      return;
    }

    handleCallback(code, state ?? '');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCallback = async (code: string, state: string) => {
    try {
      const result = await authnStore.HandleOAuth2Callback({
        entity: {} as never,
        provider_id: '',
        code,
        state,
        error: '',
        error_description: '',
      });

      // Save tokens to auth store
      if (result.access_token) {
        authStore.setCredentials({
          access_token: result.access_token,
          refresh_token: result.refresh_token,
          user_id: result.user?.entity?.id ?? '',
          profile_id: '',
          expires_in: result.expires_in,
          two_factor_required: false,
        });
        if (result.user) {
          authStore.setUser(result.user);
        }
      }

      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err);
    }
  };

  if (error) {
    return (
      <AuthLayout>
        <div className="w-full max-w-md mx-auto">
          <div className="mb-4">
            <ErrorAlert error={error} />
          </div>
          <button
            onClick={() => navigate('/login')}
            className="w-full py-3 bg-primary hover:bg-primary/90 text-white font-bold rounded-xl transition-all"
          >
            {t('auth.backToLogin')}
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-md mx-auto text-center">
        <div className="flex items-center justify-center gap-2 text-muted-foreground">
          <svg className="animate-spin size-5" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span className="text-sm">{t('auth.processingOAuth')}</span>
        </div>
      </div>
    </AuthLayout>
  );
});

export default OAuthCallbackPage;
